'use client';

import { useEffect, useId, useRef, useState, useSyncExternalStore, type ComponentProps, type ReactNode } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { twMerge } from 'tailwind-merge';
import { Button } from './Button';
import { useNavigationPrompt } from './NavigationBlocker';
import { useTranslation } from '../lib/i18n/TranslationContext';
import { Loader2, OctagonAlert } from 'lucide-react';

const FORA = { scale: 0.7, y: -48 };
const ENTRA = [0.23, 1, 0.32, 1] as const;
const SAI = [0.68, 0, 0.77, 0] as const;

const botaoBarra = 'gap-1.5 !text-xs !px-3 !py-1.5';

export function ActionBarButton({ className, ...props }: ComponentProps<typeof Button>) {
	return <Button variant='ghost' {...props} className={twMerge(botaoBarra, 'text-white hover:bg-white/10', className)} />;
}

export type AcaoDaBarra = 'salvar' | 'cancelar';

export type TextosDaActionBar = {
	mainText?: string;
	savingText?: string;
	cancelingText?: string;
	blockedText?: string;
	errorText?: string;
};

export type ActionBarProps = {
	open: boolean;
	onSave: (proceed: (redirect?: boolean) => void) => void | Promise<void>;
	onCancel?: () => void | Promise<void>;
	onError?: (erro: unknown, acao: AcaoDaBarra) => void;
	textos?: TextosDaActionBar;
	savingLabel?: ReactNode;
	cancelingLabel?: ReactNode;
	className?: string;
	children?: ReactNode;
	ignorarIds?: string[];
};

let config: ActionBarProps | null = null;
const registros = new Map<string, ActionBarProps>();
const listeners = new Set<() => void>();

const recalcular = () => {
	const todas = Array.from(registros.values());
	config = todas.find((barra) => barra.open === true) ?? todas[todas.length - 1] ?? null;
};

const subscribe = (l: () => void) => {
	listeners.add(l);
	return () => {
		listeners.delete(l);
	};
};
const notify = () => listeners.forEach((l) => l());
const getConfig = () => config;
const getServerConfig = (): ActionBarProps | null => null;

export function ActionBar(props: ActionBarProps) {
	const id = useId();

	// sem array de dependências: os callbacks e o children mudam de identidade a cada render
	useEffect(() => {
		registros.set(id, props);
		recalcular();
		notify();
	});

	useEffect(() => {
		return () => {
			registros.delete(id);
			recalcular();
			notify();
		};
	}, [id]);

	return null;
}

export function podeNavegarPara(id: string) {
	if (config?.open !== true) return true;
	return (config.ignorarIds ?? []).includes(id);
}

export const actionBarAberta = () => config?.open === true;
const getServerAberta = () => false;

export function useActionBarAberta() {
	return useSyncExternalStore(subscribe, actionBarAberta, getServerAberta);
}

export function ActionBarSlot() {
	const { t } = useTranslation();
	const cfg = useSyncExternalStore(subscribe, getConfig, getServerConfig);
	const [tremendo, setTremendo] = useState(false);
	const [acao, setAcao] = useState<AcaoDaBarra | null>(null);
	const [erro, setErro] = useState<string | null>(null);
	const ocupada = acao !== null;
	const aberta = cfg?.open === true || ocupada;
	const reduzirMovimento = useReducedMotion();

	const { pending, attempts, takeNavigation } = useNavigationPrompt(aberta);

	const ultimaTentativa = useRef(attempts);
	useEffect(() => {
		if (attempts === ultimaTentativa.current) return;
		ultimaTentativa.current = attempts;
		setTremendo(true);
		setErro(null);
	}, [attempts]);

	useEffect(() => {
		if (!pending || !aberta) setTremendo(false);
	}, [pending, aberta]);

	useEffect(() => {
		if (!aberta) setErro(null);
	}, [aberta]);

	const avisarErro = (e: unknown, qual: AcaoDaBarra) => {
		const padrao = qual === 'salvar' ? t('Não foi possível salvar') : t('Não foi possível cancelar');

		setErro(cfg?.textos?.errorText ?? (e instanceof Error && e.message ? e.message : padrao));
		cfg?.onError?.(e, qual);
	};

	const salvar = async () => {
		if (ocupada) return;

		const seguir = takeNavigation();
		const proceed = (redirect = true) => {
			if (redirect) seguir();
		};

		setErro(null);
		setAcao('salvar');

		try {
			await cfg?.onSave(proceed);
		} catch (e) {
			avisarErro(e, 'salvar');
		} finally {
			setAcao(null);
		}
	};

	const cancelar = async () => {
		if (ocupada) return;

		setErro(null);
		setAcao('cancelar');

		try {
			await cfg?.onCancel?.();
			takeNavigation();
		} catch (e) {
			avisarErro(e, 'cancelar');
		} finally {
			setAcao(null);
		}
	};

	const salvarRef = useRef(salvar);
	salvarRef.current = salvar;

	useEffect(() => {
		if (!aberta) return;

		const atalho = (e: KeyboardEvent) => {
			if (!(e.ctrlKey || e.metaKey) || e.key.toLowerCase() !== 's') return;

			e.preventDefault();
			salvarRef.current();
		};

		window.addEventListener('keydown', atalho);

		return () => window.removeEventListener('keydown', atalho);
	}, [aberta]);

	const textos = cfg?.textos;
	const padrao = textos?.mainText ?? t('Alterações não salvas');
	const mensagem = erro ?? (acao === 'salvar' ? textos?.savingText : acao === 'cancelar' ? textos?.cancelingText : tremendo ? textos?.blockedText : null) ?? padrao;

	return (
		<AnimatePresence initial={false}>
			{cfg && aberta ? (
				<motion.div
					key='empreender-action-bar'
					role='status'
					aria-live='polite'
					onAnimationEnd={(e) => {
						if (e.currentTarget === e.target) setTremendo(false);
					}}
					initial={FORA}
					animate={{ scale: 1, y: 0 }}
					exit={{ ...FORA, transition: { duration: reduzirMovimento ? 0 : 0.2, ease: SAI } }}
					transition={{ duration: reduzirMovimento ? 0 : 0.2, ease: ENTRA }}
					style={{ transformOrigin: 'top center' }}
					className={twMerge(
						'flex flex-row items-center gap-5 !py-1 !pl-2 !pr-1 w-full !rounded-[12px] shadow-[0px_4px_6px_-1px_rgba(0,0,0,0.1)] transition-colors',
						tremendo || erro ? 'bg-[#641723]' : 'bg-gray-900',
						tremendo && 'empreender-shake',
						cfg.className
					)}>
					<div className='flex flex-row items-center gap-2 flex-1 min-w-0 text-white font-sans text-xs not-italic font-medium leading-4 tracking-normal'>
						<OctagonAlert size={16} />
						<p className='flex-1 min-w-0 truncate '>{mensagem}</p>
					</div>

					<div className="flex *:flex-row items-center gap-1">
						{cfg.children}
						<Button onClick={cancelar} disabled={ocupada} className={twMerge(botaoBarra, 'bg-white/10 text-white hover:bg-white/20 !w-[74px]')}>
							{acao === 'cancelar' ? cfg.cancelingLabel ?? <Loader2 className='size-4 animate-spin' /> : t('Cancelar')}
						</Button>
						<Button variant='secondary' onClick={salvar} disabled={ocupada} className={twMerge(botaoBarra, '!w-[58px]')}>
							{acao === 'salvar' ? cfg.savingLabel ?? <Loader2 className='size-4 animate-spin' /> : t('Salvar')}
						</Button>
					</div>
				</motion.div>
			) : null}
		</AnimatePresence>
	);
}
